import { useCallback, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useParams, useRevalidator } from "react-router-dom";

import Button from "components/Button";
import CouponCodeInput from "components/CouponCodeInput";
import Form from "components/Form";
import FormSubmittingSpinner from "components/FormSubmittingSpinner";

import { useUser } from "routes/dataroutes/UserData";

import Modal, {
  getTotalPriceToInvoice,
  isCouponError,
  ModalButtonsSection,
  ModalContent,
  ModalSections,
  useCloseModal
} from "./index";
import { ErrorMessage } from "./ErrorMessage";

import scraperApi from "api";
import { fmtCurrency } from "utils";
import { isLegacyAnnualPlan } from "utils/planUtils";
import { usePlanHelpers } from "routes/data-routes/billing-data/plans/usePlanHelpers";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";


export default function OldUpgradeSubscriptionModal() {
  let { planId } = useParams() as { planId: string };

  const methods = useForm();
  const { setError, formState } = methods;
  const [ error, setFormError ] = useState<string>();
  const revalidator = useRevalidator();

  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const { activeCoupons } = useActiveCoupons();
  const { getPlan } = usePlanHelpers();
  const targetPlan = getPlan(planId);
  const price = getTotalPriceToInvoice(targetPlan, activeSubscription);

  const goBack = useCloseModal({ blockNavigationIf: methods.formState.isSubmitting });
  const onSubmit = useCallback(
    async (data: FieldValues) => {
      setFormError(undefined);
      if (activeCoupons?.find(c => c.coupon_code === data.couponCode)) {
        return setError("coupon", {
          type: "custom",
          message: "That coupon has already been used"
        });
      }

      try {
        await scraperApi.subscription.update(planId, [ data.couponCode ], false);
        revalidator.revalidate();
        goBack();
      } catch (error) {
        const err = error as any;
        if (isCouponError(err)) {
          setError("coupon", {
            type: "custom",
            message: err.details?.message || err.message
          });
        } else {
          setFormError(err.message || "There was an error upgrading your subscription");
        }
      }
    },
    [ goBack, revalidator, planId, setError, activeCoupons ]
  );

  const closeModal = useCloseModal({ blockNavigationIf: methods.formState.isSubmitting });


  const isSubmitButtonDisabled = formState.isSubmitting || (formState.isSubmitted && (error === undefined) && (Object.keys(formState.errors).length === 0));

  return (
    <Modal headline="Upgrade Subscription" onClose={ closeModal } >
      <Form methods={ methods } onSubmit={ onSubmit }>
        <ModalContent>
          <ModalSections>
            <div>
              Are you sure you want to upgrade your subscription to{ " " }
              <span className="font-bold capitalize">
                { targetPlan?.planName.replace("202206", "").trim() }
              </span>
              ?
            </div>
            <div>
              By upgrading your subscription, we will reset your credit
              counters, Invoice{ " " }
              <span className="font-bold">
                { fmtCurrency(price / 100) }{ " " }
              </span>{ " " }
              now and set your billing date to today.
            </div>
            { activeSubscription?.capabilities?.coupons && (user?.canUseAllCoupons || (user?.canUseCoupons && !isLegacyAnnualPlan(planId))) && (
              // CouponCodeInput will use the active coupons by default (calls `useActiveCoupons`)
              <CouponCodeInput
                targetPlanSlug={ planId }
              />
            ) }
            { ((user?.canUseAllCoupons === false) && isLegacyAnnualPlan(planId) && ((activeCoupons?.length || 0) > 0)) && (
              <div>
                Your current extra discount coupons will also be removed after the upgrade.
              </div>
            ) }
            <ErrorMessage errorMessage={ error } closeModalCallback={ goBack }/>
          </ModalSections>
          <ModalButtonsSection>
            <Button text="Cancel" className="button button-secondary" onClick={ goBack } size="MD"/>
            {/* TODO replace with SubmitButton (form should also be changed to a standard form) */}
            {/* submit button should be disabled while submitting the form, or when the upgrade was successful but the modal is did not get closed yet */}
            <Button
              text="Upgrade Subscription"
              className="button button-primary"
              type="submit"
              icon={ {
                Icon: FormSubmittingSpinner,
                /* TODO when the button is disabled while submitting the form, the spinner is not visible (white on white background).. still not visible with the style updated */
                style: isSubmitButtonDisabled ? "!text-brandPrimary" : ""
              } }
              disabled={ isSubmitButtonDisabled }
              size="MD"
            />
          </ModalButtonsSection>
        </ModalContent>
      </Form>
    </Modal>
  );
};
